/**
 * ToolCrossSell
 * ---------------------------------------------------------------------------
 * One suggestion, never a wall of them: a member inside a tool sees a single
 * thin nudge toward the next tool their profile makes relevant. Dismissible,
 * and the dismissal sticks per tool.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { X, ArrowRight } from 'lucide-react';
import { TOOL_LANDINGS, type ToolSlug } from '@/config/toolLandings';

const dismissKey = (slug: string) => `psyforge_crosssell_dismissed_${slug}`;

export const ToolCrossSell: React.FC<{ slug: ToolSlug }> = ({ slug }) => {
  const tool = TOOL_LANDINGS[slug];
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(dismissKey(slug)) === '1';
    } catch {
      return false;
    }
  });

  if (dismissed || !tool?.crossSell) return null;

  const dismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(dismissKey(slug), '1');
    } catch {
      /* noop */
    }
  };

  return (
    <div className="mx-auto max-w-5xl px-4 pt-4">
      <div className="flex items-center gap-3 rounded-lg border border-border bg-card/60 px-3 py-2 text-sm">
        <Link
          to={tool.crossSell.path}
          className="flex-1 min-w-0 flex items-center gap-2 group"
        >
          <span className="font-medium text-foreground shrink-0">{tool.crossSell.label}</span>
          <span className="text-muted-foreground truncate">{tool.crossSell.reason}</span>
          <ArrowRight className="w-4 h-4 text-muted-foreground shrink-0 group-hover:translate-x-0.5 transition-transform" />
        </Link>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss suggestion"
          className="p-1 rounded text-muted-foreground hover:text-foreground"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default ToolCrossSell;
